<template>
  <div class="price-check">
    <!-- The form is server-rendered in home-banner.twig and stays a plain GET so
         it works without JavaScript. This component takes over submit and renders
         the answer in place, which is the whole point: the old flow threw the
         visitor onto a different domain on their very first click. -->

    <p v-if="error" class="price-check__error" role="alert">{{ error }}</p>

    <section v-if="loading" class="price-check__result price-check__skeleton" aria-hidden="true">
      <div class="skeleton-line skeleton-line--title"></div>
      <div class="skeleton-line skeleton-line--sub"></div>

      <ul class="price-check__grades">
        <li v-for="n in 3" :key="n" class="price-check__grade">
          <div class="skeleton-line skeleton-line--label"></div>
          <div class="skeleton-line skeleton-line--price"></div>
        </li>
      </ul>

      <div class="price-check__chart skeleton-block"></div>
    </section>

    <!-- The skeleton is decorative, so the announcement is made separately rather than
         read out as a pile of empty boxes. -->
    <p v-if="loading" class="sr-only" role="status" aria-live="polite">
      Looking up {{ pendingMicron }} micron prices.
    </p>

    <section v-if="result" class="price-check__result" aria-live="polite">
      <h2 class="price-check__title">
        {{ result.micron }} micron, last {{ result.days }} days
      </h2>
      <p class="price-check__sub label-micro">
        {{ result.length_label }} fleece, {{ result.micron_low }} to {{ result.micron_high }} micron.
        Prices are NZ$ per kg clean.
      </p>

      <ul class="price-check__grades">
        <li v-for="grade in result.series" :key="grade.key" class="price-check__grade">
          <span class="label-micro price-check__grade-name">
            <!-- Colour comes straight from the API (grade.colour), which is the
                 same value the Flask tool paints its chart lines with, so the two
                 tools cannot drift apart if that palette is ever changed upstream.
                 Decorative: the label and the sr-only range carry the meaning. -->
            <span class="price-check__swatch"
                  :style="{ background: grade.colour }"
                  aria-hidden="true"></span>
            <!-- Split so a phone can show "Good" where "Good colour" does not fit
                 across a third of the width. The word dropped is the one every
                 column repeats, and the sr-only range below still says "colour",
                 so nothing is lost to a screen reader. -->
            <span class="price-check__grade-label"><!--
              --><span>{{ gradeWord(grade.label) }}</span><!--
              --><span v-if="gradeRest(grade.label)" class="price-check__grade-rest">{{ gradeRest(grade.label) }}</span><!--
            --></span>
            <span class="sr-only">, Y-Z colour {{ grade.yz_min }} to {{ grade.yz_max }}</span>
          </span>

          <!-- Price and change on one line: three columns of stacked figures ran
               the panel past a phone screen. -->
          <span class="price-check__grade-figures">
            <span class="price-check__grade-price tnum">
              ${{ formatPrice(grade.clean.average) }}
            </span>
            <span
                class="price-check__grade-change tnum"
                :class="grade.clean.change_pct >= 0 ? 'delta-positive' : 'delta-negative'"
                v-if="grade.clean.change_pct !== null"
            >
              {{ grade.clean.change_pct >= 0 ? '+' : '' }}{{ grade.clean.change_pct }}%
            </span>
          </span>
          <!-- confidence: 'low' means this band traded too rarely at this micron to
               learn its own relationship and a broader one was borrowed. The API
               says so, so the panel says so. -->
          <span v-if="grade.confidence === 'low'" class="price-check__flag label-micro">
            Indicative
          </span>
        </li>
      </ul>

      <div class="price-check__chart" role="img" :aria-label="chartSummary">
        <Chart :labels="chartLabels" :data="chartSeries" unit="dollars"/>
      </div>

      <!-- Same capture, same event, same destination as the Flask tool: the
           subscriber list is stored by the Laravel app, and its /api/preview
           route is CORS-enabled and CSRF-free for exactly this call. -->
      <form v-if="!leadDone" class="price-check__lead" @submit.prevent="submitLead">
        <label for="price-check-email" class="label-micro">
          Want these emailed after every sale?
        </label>
        <div class="price-check__lead-row">
          <input
              id="price-check-email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              spellcheck="false"
              placeholder="your@email.co.nz"
              class="price-check__input">
          <button type="submit" class="button primary" :disabled="leadSending">
            {{ leadSending ? 'Sending...' : 'Email me' }}
          </button>
        </div>
        <p v-if="leadError" class="price-check__error" role="alert">{{ leadError }}</p>
      </form>
      <p v-else class="price-check__lead-done">
        Done. We will email you {{ result.micron }} micron prices after every sale.
      </p>

    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import Chart from './components/Chart.vue';
import { trackSearch, trackLead, trackCtaViewed } from '../js/pixel';

export default {
  components: { Chart },

  props: {
    /* Same-origin Craft actions, not the dataengine directly. Going cross-origin
     * needed a CORS allowlist that could never include the ddev host, and a local
     * dataengine is http against this https page, so the browser blocked it as
     * mixed content regardless. See PreviewController. */
    pricesUrl: { type: String, default: '' },
    subscribeUrl: { type: String, default: '' },
    /* Craft enforces CSRF on POST, so the subscribe call carries the token. */
    csrfName: { type: String, default: '' },
    csrfToken: { type: String, default: '' },
    /* Where "see the full tool" goes. Dev-aware, same as the form action. */
    appBase: { type: String, default: '' },
  },

  setup(props) {
    const loading = ref(false);
    const error = ref('');
    const result = ref(null);
    const pendingMicron = ref('');
    const email = ref('');
    const leadSending = ref(false);
    const leadError = ref('');
    const leadDone = ref(false);

    /* The preview API returns dollars per kg clean already, unlike the Strong Wool
     * Index API which reports cents. No division here. */
    const formatPrice = (dollars) => (dollars === null || dollars === undefined)
        ? '-'
        : Number(dollars).toFixed(2);

    /* "Good colour" splits into "Good" and " colour". The API owns the wording, so
     * this takes the first word rather than matching on a list: whatever it sends,
     * the first word is the grade and the rest is the qualifier every column shares. */
    const gradeWord = (label) => String(label || '').split(' ')[0];
    const gradeRest = (label) => {
      const parts = String(label || '').split(' ');
      return parts.length > 1 ? ` ${parts.slice(1).join(' ')}` : '';
    };

    /* The chart plots the middle (average colour) band. Three overlaid lines in a
     * hero panel is a chart nobody reads; the three averages above already carry
     * the comparison. */
    const chartSeries = computed(() => {
      if (!result.value) return [];
      const mid = result.value.series[1] || result.value.series[0];
      return mid ? mid.clean.prices : [];
    });
    /* Told to Chart.vue explicitly; it assumes cents otherwise. */

    const chartLabels = computed(() => {
      if (!result.value) return [];
      const mid = result.value.series[1] || result.value.series[0];
      return mid ? mid.labels : [];
    });

    const chartSummary = computed(() => {
      if (!result.value) return '';
      const mid = result.value.series[1] || result.value.series[0];
      if (!mid) return '';
      return `Line chart of ${mid.label} ${result.value.micron} micron wool prices `
          + `over the last ${result.value.days} days, averaging `
          + `$${formatPrice(mid.clean.average)} per kg clean.`;
    });

    const lookup = async (micron) => {
      loading.value = true;
      error.value = '';
      result.value = null;
      leadDone.value = false;
      pendingMicron.value = micron;

      try {
        /* GET because it is a read, which also means no CSRF token is needed.
         * The proxy turns it into the POST the dataengine wants. */
        const separator = props.pricesUrl.includes('?') ? '&' : '?';
        const url = `${props.pricesUrl}${separator}micron=${encodeURIComponent(micron)}`;
        const response = await fetch(url, {
          headers: { 'Accept': 'application/json' },
        });

        const body = await response.json();

        if (!response.ok) {
          /* The API's own message is more useful than anything generic: it names
           * the micron range that actually has data. */
          error.value = body.error || 'Could not load prices right now.';
          return;
        }

        result.value = body;
        trackSearch(micron);
        /* Named for the CTA that used to sit under this result. The button is gone but the
         * event still marks the same funnel step, a visitor seeing real prices, so it keeps
         * firing rather than taking a stage of Meta reporting with it. Worth renaming on
         * both sides together: the same event names live in the Flask and Laravel apps. */
        trackCtaViewed(micron);
      } catch (e) {
        error.value = 'Could not load prices right now. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const submitLead = async () => {
      leadSending.value = true;
      leadError.value = '';

      try {
        const payload = {
          email: email.value,
          micron: result.value ? result.value.micron : null,
        };
        if (props.csrfName) {
          payload[props.csrfName] = props.csrfToken;
        }

        const response = await fetch(props.subscribeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const body = await response.json();

        if (!response.ok) {
          leadError.value = body.error || 'Could not save that.';
          return;
        }

        leadDone.value = true;
        trackLead(email.value, result.value ? result.value.micron : null);
      } catch (e) {
        leadError.value = 'Could not save that right now.';
      } finally {
        leadSending.value = false;
      }
    };

    onMounted(() => {
      const form = document.querySelector('.micron-check');
      const input = document.getElementById('micron');
      if (!form || !input) return;

      /* The button lives in home-banner.twig, outside this component, because the form
       * has to work as a plain GET with JavaScript off. Same reach-out as the form and
       * input above. */
      const button = form.querySelector('button[type="submit"]');
      const buttonLabel = button ? button.textContent : '';

      watch(loading, (busy) => {
        if (!button) return;
        button.disabled = busy;
        button.textContent = busy ? 'Checking...' : buttonLabel;
      });

      form.addEventListener('submit', (event) => {
        /* Let the browser's own validation run first, and fall through to the
         * plain GET if anything here is not usable. */
        if (!input.checkValidity()) return;

        event.preventDefault();
        lookup(input.value);
      });

      /* Deep links (?micron=35) run the lookup on arrival, matching the ?auto=
       * behaviour the Flask tool already has. */
      try {
        const micron = new URLSearchParams(window.location.search).get('micron');
        if (micron && Number(micron) >= 12 && Number(micron) <= 45) {
          input.value = micron;
          lookup(micron);
        }
      } catch (e) { /* no-op */ }
    });

    return {
      loading, error, result, pendingMicron,
      email, leadSending, leadError, leadDone,
      chartSeries, chartLabels, chartSummary,
      formatPrice, gradeWord, gradeRest, submitLead,
    };
  },
};
</script>
